import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Database, Trash, ArrowsClockwise, Warning } from "@phosphor-icons/react";

function formatBytes(n) {
  if (!n || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function DbStatsDialog({ open, onOpenChange }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cleanupBefore, setCleanupBefore] = useState("");
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/db-stats");
      setStats(data);
    } catch (e) {
      toast.error("Failed to load DB stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      load();
      setCleanupPreview(null);
      setCleanupBefore("");
    }
  }, [open]);

  const runDryRun = async () => {
    if (!cleanupBefore) {
      toast.error("Pick a cutoff date first");
      return;
    }
    setCleanupBusy(true);
    try {
      const iso = new Date(cleanupBefore + "T00:00:00Z").toISOString();
      const { data } = await api.post("/admin/cleanup/old-messages", {
        before: iso,
        dry_run: true,
      });
      setCleanupPreview({ count: data.would_delete, iso });
    } catch (_) {
      toast.error("Preview failed");
    } finally {
      setCleanupBusy(false);
    }
  };

  const runRealDelete = async () => {
    if (!cleanupPreview) return;
    if (!window.confirm(
      `Delete ${cleanupPreview.count} messages before ${formatDate(cleanupPreview.iso)}? This cannot be undone.`,
    )) return;
    setCleanupBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup/old-messages", {
        before: cleanupPreview.iso,
        dry_run: false,
      });
      toast.success(`Deleted ${data.deleted} messages`);
      setCleanupPreview(null);
      setCleanupBefore("");
      load();
    } catch (_) {
      toast.error("Delete failed");
    } finally {
      setCleanupBusy(false);
    }
  };

  const percent = stats?.percent_used ?? 0;
  const barColor =
    percent >= 85 ? "bg-[#EF4444]" : percent >= 60 ? "bg-[#F59E0B]" : "bg-[#10B981]";

  const totalStorage = stats?.storage_size ?? 0;
  const collections = (stats?.collections || []).slice().sort((a, b) => b.size - a.size);
  const maxColSize = Math.max(1, ...collections.map((c) => c.size));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="db-stats-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database size={18} weight="fill" />
            Database storage
          </DialogTitle>
          <DialogDescription>
            Live view of your Mongo storage, collection sizes, and old-message cleanup.
          </DialogDescription>
        </DialogHeader>

        {/* Total usage bar */}
        <div className="space-y-2">
          <div className="flex items-end justify-between gap-2">
            <div>
              <div className="text-xs text-[var(--muted)]">Used storage</div>
              <div className="text-2xl font-semibold">{formatBytes(totalStorage)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-[var(--muted)]">of soft limit</div>
              <div className="text-sm font-medium">
                {formatBytes(stats?.soft_limit_bytes || 0)} · {percent}%
              </div>
            </div>
          </div>
          <div className="h-2 rounded-full bg-[var(--bg)] overflow-hidden">
            <div
              className={`h-full ${barColor} transition-[width]`}
              style={{ width: `${Math.min(100, Math.max(2, percent))}%` }}
              data-testid="db-usage-bar"
            />
          </div>
          <div className="flex flex-wrap gap-2 text-[11px] text-[var(--muted)]">
            <span>
              Objects: <strong className="text-[var(--fg)]">{(stats?.objects || 0).toLocaleString()}</strong>
            </span>
            <span>•</span>
            <span>Data: {formatBytes(stats?.data_size || 0)}</span>
            <span>•</span>
            <span>Indexes: {formatBytes(stats?.index_size || 0)}</span>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 ml-auto text-[11px]"
              onClick={load}
              disabled={loading}
              data-testid="db-stats-refresh"
            >
              <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Collections breakdown */}
        <div className="mt-4">
          <div className="text-xs font-semibold mb-2">Collections</div>
          <div className="max-h-56 overflow-auto space-y-1.5 pr-2">
            {collections.map((c) => {
              const w = Math.max(4, (c.size / maxColSize) * 100);
              return (
                <div key={c.name} className="text-[11px]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono truncate">{c.name}</span>
                    <span className="text-[var(--muted)] shrink-0">
                      {c.count.toLocaleString()} · {formatBytes(c.size)}
                    </span>
                  </div>
                  <div className="h-1 rounded-full bg-[var(--bg)] overflow-hidden mt-0.5">
                    <div
                      className="h-full bg-[var(--fg)]/60"
                      style={{ width: `${w}%` }}
                    />
                  </div>
                </div>
              );
            })}
            {collections.length === 0 && (
              <div className="text-[11px] text-[var(--muted)]">No collections</div>
            )}
          </div>
        </div>

        {/* Cleanup tool */}
        <div className="mt-4 rounded-md border border-[var(--border)] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Trash size={14} weight="bold" />
            <div className="text-xs font-semibold">Old-message cleanup</div>
            <Badge variant="outline" className="text-[9px] h-4 ml-auto">
              Admin only
            </Badge>
          </div>
          <div className="text-[11px] text-[var(--muted)] mb-3">
            Oldest message: <strong className="text-[var(--fg)]">{formatDate(stats?.messages_oldest)}</strong>
            {" · "}Newest: <strong className="text-[var(--fg)]">{formatDate(stats?.messages_newest)}</strong>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label htmlFor="cleanup-before" className="text-[11px]">
                Delete messages older than
              </Label>
              <Input
                id="cleanup-before"
                type="date"
                value={cleanupBefore}
                onChange={(e) => {
                  setCleanupBefore(e.target.value);
                  setCleanupPreview(null);
                }}
                data-testid="cleanup-before-input"
                className="h-8 text-xs"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={runDryRun}
              disabled={cleanupBusy || !cleanupBefore}
              data-testid="cleanup-preview-btn"
              className="h-8"
            >
              Preview count
            </Button>
          </div>
          {cleanupPreview && (
            <div className="mt-3 rounded-md bg-[#FEF3C7]/40 dark:bg-[#78350F]/20 border border-[#F59E0B]/30 p-2.5">
              <div className="flex items-center gap-2 text-xs">
                <Warning size={14} weight="fill" className="text-[#B45309]" />
                <span className="flex-1">
                  <strong>{cleanupPreview.count.toLocaleString()}</strong>{" "}
                  messages will be permanently deleted (before{" "}
                  <strong>{formatDate(cleanupPreview.iso)}</strong>).
                </span>
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setCleanupPreview(null)}
                  disabled={cleanupBusy}
                  className="h-7"
                  data-testid="cleanup-cancel"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={runRealDelete}
                  disabled={cleanupBusy || cleanupPreview.count === 0}
                  className="h-7"
                  data-testid="cleanup-confirm"
                >
                  {cleanupBusy ? "Deleting…" : "Delete permanently"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
